'use client'
import { ImageWithFallback } from '@/components'
import CustomSwiper from '@/components/custom-swiper'
import { RiInformationLine, RiMapPinLine } from '@remixicon/react'
import { FaStar } from 'react-icons/fa'
import { LuBath, LuBedDouble, LuUsers } from 'react-icons/lu'

const appendUniqueImage = (images, imageUrl) => {
  if (imageUrl && !images.includes(imageUrl)) {
    images.push(imageUrl)
  }
}

const addPhotoSet = (images, photos) => {
  if (!Array.isArray(photos)) {
    return
  }

  photos.forEach((item) => {
    appendUniqueImage(
      images,
      item?.medium_img_url || item?.small_img_url || item?.original_img_url,
    )
  })
}

export const getValidImages = (property, similarproperty) => {
  const images = []

  addPhotoSet(images, property?.other_photos)

  if (similarproperty) {
    addPhotoSet(images, property?.all_photos)
  }

  if (images.length === 0) {
    appendUniqueImage(images, property?.main_photo)
  }

  if (similarproperty && images.length === 0) {
    appendUniqueImage(images, property?.thumbnail)
  }

  return images
}

export const isModifiedNavigationEvent = (event) => (
  event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0
)

const getImageWrapperClassName = (ishome, view) => (
  `${ishome
    ? 'w-full rounded-[20px] overflow-hidden'
    : view === 'grid'
      ? 'rounded-t-[20px] w-full md:w-full'
      : 'w-full sm:w-[293px] rounded-xl md:w-[150px]'
  } aspect-square overflow-hidden relative`
)

const getDisplayPriceDailyRate = ({
  ishome,
  similarproperty,
  property,
  displayPrice,
}) => {
  if (ishome) {
    return undefined
  }

  if (similarproperty) {
    return property?.daily_rate ? displayPrice(property?.daily_rate ?? 0) : 0
  }

  return displayPrice(property?.daily_rate ?? 0) || 0
}

export const PropertyImageSection = ({
  validImages,
  property,
  similarproperty,
  isDesktop,
  enableTouch,
  isFullyVisible,
  ishome,
  view,
  displayPrice,
  onDragStart,
  onDragMove,
  onDragEnd,
}) => (
  <div
    className={getImageWrapperClassName(ishome, view)}
    onMouseDown={(event) => isDesktop && onDragStart(event)}
    onMouseMove={(event) => isDesktop && onDragMove(event)}
    onMouseUp={() => isDesktop && onDragEnd()}
    onMouseLeave={() => isDesktop && onDragEnd()}
    onTouchStart={(event) => !isDesktop && onDragStart(event)}
    onTouchMove={(event) => !isDesktop && onDragMove(event)}
    onTouchEnd={() => !isDesktop && onDragEnd()}
    onTouchCancel={() => !isDesktop && onDragEnd()}
  >
    <CustomSwiper
      autoplay={isFullyVisible && !isDesktop}
      autoplaySpeed={4000}
      showDots={validImages.length > 1}
      touchEnabled={isDesktop || enableTouch}
      className='w-full h-full'
      activeDotColor='bg-[#FFFFFF]'
      inactiveDotColor='bg-white'
      maxDots={6}
      ishome={ishome}
      dotClassName='bottom-4'
      dotSize={8}
      displayPriceDailyRate={getDisplayPriceDailyRate({
        ishome,
        similarproperty,
        property,
        displayPrice,
      })}
    >
      {validImages.map((imageUrl, index) => (
        <ImageWithFallback
          key={index}
          alt={property?.name || 'Property image'}
          width={similarproperty ? 289 : 500}
          height={similarproperty ? 289 : 500}
          src={imageUrl}
          sizes={
            similarproperty
              ? '(min-width: 1024px) 25vw, (min-width: 640px) 33vw, 100vw'
              : '(min-width: 1280px) 25vw, (min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw'
          }
          className='w-full h-full object-cover bg-gray-300 overflow-hidden'
          priority={index === 0}
        />
      ))}
    </CustomSwiper>
  </div>
)

export const PropertyLocation = ({ cityName, onCityClick }) => (
  <span
    role='link'
    tabIndex={0}
    onClick={onCityClick}
    onKeyDown={(event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        onCityClick(event)
      }
    }}
    aria-label={`View properties in ${cityName}`}
    className='flex gap-1 items-center group/city cursor-pointer flex-1 min-w-0'
  >
    <RiMapPinLine className='text-[#80c7e2] w-4 h-4 flex-shrink-0' />
    <span className='text-[12px] text-[#5F6369] truncate group-hover/city:text-[#191919] group-hover/city:underline underline-offset-2 transition-colors'>
      {cityName}
    </span>
  </span>
)

const HomePropertyReviews = ({ property }) => (
  <div className='flex items-center gap-1 bg-[#F6F6F6] rounded-full px-2 py-0.5 flex-shrink-0'>
    <FaStar className='text-[#FFA125] text-[11px]' />
    <span className='text-[12px] font-medium text-[#191919]'>
      {property?.reviews_count > 0
        ? (property?.overall_rating ?? property?.overall_reviews ?? 0).toFixed(1)
        : 'New'}
    </span>
  </div>
)

const getSimilarPropertyReviewText = (property) => {
  if (!(property?.reviews_count > 0)) {
    return 'New'
  }

  return `${property?.overall_reviews ? property?.overall_reviews.toFixed(1) : '0'} (${property?.review_count || property?.reviews_count})`
}

const getListingPropertyReviewText = (property) => {
  if (!(property?.reviews_count > 0)) {
    return 'New'
  }

  const rating = property?.overall_rating
    ? property.overall_rating.toFixed(1)
    : property?.overall_reviews
      ? property?.overall_reviews.toFixed(1)
      : '0'

  return `${rating} (${property.reviews_count}${property.reviews_count === 1 ? ' review' : ' reviews'})`
}

const ListingPropertyReviews = ({ property, similarproperty }) => {
  const hasReviews = property?.reviews_count > 0
  const reviewText = similarproperty
    ? getSimilarPropertyReviewText(property)
    : getListingPropertyReviewText(property)

  return (
    <div
      className={`flex items-center rounded-[21px] px-2.5 py-0.5 border bg-[#FFFFFF] border-[#EDEDED] flex-shrink-0 ${hasReviews ? 'min-w-[127px]' : 'w-fit'}`}
    >
      <FaStar className='text-[#FFA125] font-medium text-[12px] mr-1' />
      <p className='text-[12px] font-medium text-[#191919] whitespace-nowrap'>
        {reviewText}
      </p>
    </div>
  )
}

// Key unit facts a guest needs to judge fit (group size, sleeping, bathrooms)
// without opening the full listing. Backed by the search API fields of the same
// name; a fact is dropped when the PMS has no value for it rather than showing
// a misleading "0".
const UNIT_FACTS = [
  { key: 'bedrooms', Icon: LuBedDouble, singular: 'bedroom', plural: 'bedrooms' },
  { key: 'bathrooms', Icon: LuBath, singular: 'bath', plural: 'baths' },
  { key: 'max_guests', Icon: LuUsers, singular: 'guest', plural: 'guests' },
]

const getUnitFacts = (property) =>
  UNIT_FACTS.reduce((facts, { key, Icon, singular, plural }) => {
    const count = Number(property?.[key]) || 0

    if (count > 0) {
      facts.push({
        key,
        Icon,
        label: `${count} ${count === 1 ? singular : plural}`,
      })
    }

    return facts
  }, [])

// One presentation for every card variant (home, listing, similar) so the row
// reads identically wherever a card appears. Rendered inside the card's <Link>,
// so this stays div/span based — a <ul> would be invalid markup there.
export const PropertyUnitFacts = ({ property }) => {
  const facts = getUnitFacts(property)

  if (facts.length === 0) {
    return null
  }

  return (
    <div
      className='flex items-center flex-wrap gap-x-3 gap-y-1 text-[12px] font-medium text-[#5F6369] min-h-[18px]'
      aria-label={`Unit details: ${facts.map((fact) => fact.label).join(', ')}`}
    >
      {facts.map(({ key, Icon, label }) => (
        <span key={key} className='flex items-center gap-1 whitespace-nowrap'>
          <Icon aria-hidden='true' className='w-3.5 h-3.5 flex-shrink-0 text-[#80c7e2]' />
          {label}
        </span>
      ))}
    </div>
  )
}

export const PropertyReviews = ({ ishome, property, similarproperty }) => (
  ishome
    ? <HomePropertyReviews property={property} />
    : <ListingPropertyReviews property={property} similarproperty={similarproperty} />
)

export const HomePriceSection = ({ displayPrice, property, isDiscounted, discountPct, originalPrice }) => (
  <div className='flex items-center justify-between gap-2 mt-auto pt-1'>
    <div className='flex items-baseline gap-1.5 flex-nowrap whitespace-nowrap min-w-0 overflow-hidden'>
      <span className='text-[16px] font-bold text-[#191919]'>
        {displayPrice(property?.daily_rate ?? 0)}
      </span>
      {isDiscounted && (
        <span className='text-[13px] text-[#9E9E9E] line-through'>
          {displayPrice(originalPrice)}
        </span>
      )}
      <span className='text-[12px] text-[#898989]'>/ night</span>
    </div>
    {isDiscounted && (
      <span className='text-[13px] font-bold text-[#2CA81E] bg-[#ECFAF0] rounded-full px-2.5 py-1 flex-shrink-0'>
        {discountPct}% OFF
      </span>
    )}
  </div>
)

export const NonHomePriceSection = ({
  property,
  displayPrice,
  displayAlternatePrice,
  isDiscounted,
  discountPct,
  originalPrice,
  showTooltip,
  setShowTooltip,
}) => (
    <div className='mt-auto pt-2.5'>
      <div className='flex items-center gap-1.5 flex-nowrap whitespace-nowrap'>
        <span className='text-[18px] font-bold text-[#191919] leading-none flex-shrink-0'>
          {property?.daily_rate ? displayPrice(property.daily_rate) : displayPrice(0)}
        </span>
        {isDiscounted && (
          <span className='text-[12px] text-[#9E9E9E] line-through leading-none'>
            {displayPrice(originalPrice)}
          </span>
        )}
        <span className='text-[11px] text-[#7C7C7C] leading-none'>/ night</span>
        <div
          className='relative flex items-center'
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          <RiInformationLine className='w-[13px] h-[13px] text-[#7C7C7C]' />
          {showTooltip && (
            <div className='absolute z-10 top-full mt-2 left-1/2 -translate-x-1/2 bg-[#1c1c1c] text-white text-[11px] px-3 py-1.5 rounded shadow-md whitespace-nowrap'>
              Property currency: {displayAlternatePrice(Math.round(property.daily_rate))}
              <div className='absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-[#1c1c1c] rotate-45'></div>
            </div>
          )}
        </div>
        {isDiscounted && (
          <span className='ml-auto h-6 flex items-center justify-center text-[#2CA81E] bg-[#ECFAF0] text-[13px] font-bold rounded-[30px] px-2.5'>
            {discountPct}% OFF
          </span>
        )}
      </div>
    </div>
)